import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Dialog,
  CircularProgress,
  Chip,
  IconButton,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { api } from '../services/api';

const NEON = '#DCF995';
const MUTED = 'rgba(255,255,255,0.4)';
const TEXT_WHITE = '#ffffff';
const SURFACE = 'rgba(255,255,255,0.06)';
const BORDER = 'rgba(255,255,255,0.1)';

interface EchoResult {
  question: string;
  summary: string;
  accounts: Array<{
    accountId: string;
    companyName: string;
    healthScore: number;
    onboardingStage: string;
    daysSinceLastActivity: number;
  }>;
  totalMatched: number;
}

interface EchoDialogProps {
  open: boolean;
  onClose: () => void;
}

const SUGGESTIONS = [
  'Show me at-risk accounts',
  'Which accounts have low health scores?',
  'Accounts with no orders this week',
  'Top 5 healthiest accounts',
  'How many accounts are active?',
];

/**
 * Render a model-generated summary as plain text, honouring only **bold**
 * markdown. Never inject HTML: the summary can echo customer-supplied strings.
 */
function renderBoldMarkdown(text: string): React.ReactNode[] {
  const parts = String(text ?? "").split(/\*\*(.+?)\*\*/g);
  return parts.map((part, i) => (i % 2 === 1 ? <strong key={i}>{part}</strong> : <React.Fragment key={i}>{part}</React.Fragment>));
}

export const EchoDialog: React.FC<EchoDialogProps> = ({ open, onClose }) => {
  const navigate = useNavigate();
  const [question, setQuestion] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<EchoResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Focus input when dialog opens
  useEffect(() => {
    if (open) {
      setQuestion('');
      setResult(null);
      setError(null);
      setTimeout(() => inputRef.current?.focus(), 200);
    }
  }, [open]);

  const handleSubmit = useCallback(async (q?: string) => {
    const queryText = (q || question).trim();
    if (!queryText || loading) return;

    setQuestion(queryText);
    setError(null);
    setLoading(true);
    setResult(null);

    try {
      const data = await api.echoQuery(queryText);
      setResult(data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Echo encountered an error. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [question, loading]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    }
  };

  const getHealthColor = (score: number) => {
    if (score >= 70) return '#22c55e';
    if (score >= 40) return '#f59e0b';
    return '#ef4444';
  };

  const getStageColor = (stage: string) => {
    switch (stage) {
      case 'active': return 'rgba(34,197,94,0.15)';
      case 'at_risk': return 'rgba(245,158,11,0.15)';
      case 'churned': return 'rgba(239,68,68,0.15)';
      default: return 'rgba(255,255,255,0.08)';
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: '90vw',
          maxWidth: 800,
          minHeight: 400,
          maxHeight: '80vh',
          borderRadius: '20px',
          background: 'linear-gradient(135deg, #1a1a2e 0%, #0f0a1a 50%, #1B0826 100%)',
          border: '1px solid rgba(255,255,255,0.08)',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
      slotProps={{
        backdrop: {
          sx: { backdropFilter: 'blur(8px)', backgroundColor: 'rgba(0,0,0,0.6)' },
        },
      }}
    >
      <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box>
            <Typography sx={{ fontSize: '24px', fontWeight: 700, color: TEXT_WHITE }}>
              Ask Echo
            </Typography>
            <Typography sx={{ fontSize: '14px', color: MUTED }}>
              Query your accounts using natural language
            </Typography>
          </Box>
          <IconButton onClick={onClose} sx={{ color: MUTED, '&:hover': { color: TEXT_WHITE } }}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Input */}
        <Box
          sx={{
            height: 56,
            borderRadius: '14px',
            bgcolor: SURFACE,
            border: '1px solid',
            borderColor: BORDER,
            display: 'flex',
            alignItems: 'center',
            px: 2.5,
            transition: 'border-color 0.3s, box-shadow 0.4s',
            '&:focus-within': {
              borderColor: 'rgba(220,249,149,0.25)',
              boxShadow: `
                0 0 15px 2px rgba(220,249,149,0.06),
                0 0 40px 8px rgba(220,249,149,0.04),
                inset 0 0 20px 2px rgba(220,249,149,0.02)
              `,
            },
          }}
        >
          <input
            ref={inputRef}
            value={question}
            onChange={e => setQuestion(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="e.g. Show me accounts with declining health scores..."
            style={{
              flex: 1,
              background: 'transparent',
              border: 'none',
              outline: 'none',
              color: TEXT_WHITE,
              fontSize: '15px',
              fontFamily: 'inherit',
              caretColor: NEON,
            }}
          />
          {loading ? (
            <CircularProgress size={20} sx={{ color: NEON, ml: 1 }} />
          ) : (
            <Typography
              onClick={() => handleSubmit()}
              sx={{
                fontSize: '13px',
                color: question.trim() ? NEON : MUTED,
                cursor: question.trim() ? 'pointer' : 'default',
                fontWeight: 600,
                ml: 1,
                userSelect: 'none',
              }}
            >
              Ask
            </Typography>
          )}
        </Box>

        {/* Suggestions (when no result yet) */}
        {!result && !loading && !error && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2 }}>
            {SUGGESTIONS.map(s => (
              <Chip
                key={s}
                label={s}
                size="small"
                onClick={() => { setQuestion(s); handleSubmit(s); }}
                sx={{
                  bgcolor: 'rgba(255,255,255,0.04)',
                  color: 'rgba(255,255,255,0.5)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  fontSize: '12px',
                  cursor: 'pointer',
                  '&:hover': { bgcolor: 'rgba(220,249,149,0.08)', color: NEON, borderColor: 'rgba(220,249,149,0.2)' },
                }}
              />
            ))}
          </Box>
        )}

        {/* Loading state */}
        {loading && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, mt: 4 }}>
            <Box sx={{ textAlign: 'center' }}>
              <CircularProgress size={28} sx={{ color: NEON, mb: 2 }} />
              <Typography sx={{ fontSize: '14px', color: MUTED }}>
                Echo is thinking...
              </Typography>
            </Box>
          </Box>
        )}

        {/* Error */}
        {error && (
          <Box sx={{ mt: 3, p: 2, borderRadius: '12px', bgcolor: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.15)' }}>
            <Typography sx={{ fontSize: '14px', color: 'rgba(239,68,68,0.9)' }}>
              {error}
            </Typography>
          </Box>
        )}

        {/* Results */}
        {result && (
          <Box sx={{ mt: 3, flex: 1, overflowY: 'auto', minHeight: 0 }}>
            {/* Summary */}
            <Box sx={{ mb: 3, p: 2, borderRadius: '12px', bgcolor: 'rgba(220,249,149,0.05)', border: '1px solid rgba(220,249,149,0.1)' }}>
              {/* Model output can echo customer-supplied strings; render as text,
                  never as HTML. Only **bold** markdown is honoured. */}
              <Typography sx={{ fontSize: '14px', color: TEXT_WHITE, lineHeight: 1.6, '& strong': { color: NEON } }}>
                {renderBoldMarkdown(result.summary)}
              </Typography>
              {result.totalMatched > 0 && (
                <Typography sx={{ fontSize: '12px', color: MUTED, mt: 1 }}>
                  {result.totalMatched} account{result.totalMatched !== 1 ? 's' : ''} matched
                </Typography>
              )}
            </Box>

            {/* Account list — show max 5 in dialog */}
            {result.accounts.length > 0 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {result.accounts.slice(0, 5).map(account => (
                  <Box
                    key={account.accountId}
                    onClick={() => { onClose(); navigate(`/accounts/${account.accountId}`); }}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      p: 1.5,
                      borderRadius: '10px',
                      bgcolor: SURFACE,
                      border: '1px solid',
                      borderColor: BORDER,
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                      '&:hover': {
                        borderColor: 'rgba(220,249,149,0.2)',
                        bgcolor: 'rgba(255,255,255,0.08)',
                      },
                    }}
                  >
                    <Box>
                      <Typography sx={{ fontSize: '14px', fontWeight: 600, color: TEXT_WHITE }}>
                        {account.companyName}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, mt: 0.5, alignItems: 'center' }}>
                        <Chip
                          label={account.onboardingStage}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: '11px',
                            bgcolor: getStageColor(account.onboardingStage),
                            color: TEXT_WHITE,
                          }}
                        />
                        {account.daysSinceLastActivity != null && (
                          <Typography sx={{ fontSize: '12px', color: MUTED }}>
                            {account.daysSinceLastActivity === 0 ? 'Active today' : `${account.daysSinceLastActivity}d ago`}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography sx={{
                        fontSize: '20px',
                        fontWeight: 700,
                        color: getHealthColor(account.healthScore),
                      }}>
                        {account.healthScore}
                      </Typography>
                      <Typography sx={{ fontSize: '10px', color: MUTED }}>
                        health
                      </Typography>
                    </Box>
                  </Box>
                ))}

                {/* See all button when more than 5 results */}
                {result.totalMatched > 5 && (
                  <Box
                    onClick={() => { onClose(); navigate('/accounts'); }}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      p: 1.5,
                      borderRadius: '10px',
                      border: '1px dashed',
                      borderColor: 'rgba(220,249,149,0.2)',
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                      '&:hover': {
                        borderColor: 'rgba(220,249,149,0.4)',
                        bgcolor: 'rgba(220,249,149,0.04)',
                      },
                    }}
                  >
                    <Typography sx={{ fontSize: '14px', color: NEON, fontWeight: 600 }}>
                      See all {result.totalMatched} accounts
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Dialog>
  );
};
